import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Platform, StyleSheet, View, type ViewStyle } from 'react-native';

/**
 * Web-native pinch-zoom + pan wrapper for the annotation stage.
 *
 * Gesture contract:
 *   - ONE pointer at scale === 1
 *       → propagates to children (chip taps, refine handle drags,
 *         draw-mode pointer capture, outer ScrollView scrolls).
 *         Never captured here.
 *   - ONE pointer at scale  >  1 AND drag starts on empty stage
 *       → pans the image. Tap-vs-drag disambiguation: if the
 *         pointer moves < 8px, the tap still propagates to the
 *         child; once movement crosses the threshold we capture
 *         and prevent the outer container from also scrolling.
 *   - TWO pointers
 *       → claimed here. Spread/pinch scales; midpoint translates.
 *   - Double-tap
 *       → resets to scale=1, translate=0.
 *
 * Imperative `reset()` exposed via `forwardRef` so a "Fit" button
 * in the parent can snap back without simulating a double-tap.
 *
 * `onScaleChange(scale)` fires whenever the scale crosses either
 * direction of the SNAP_BACK_THRESHOLD, so the parent can enable /
 * disable a zoom-reset control without polling.
 */

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const SNAP_BACK_THRESHOLD = 1.02;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_SLOP_PX = 24;
const PAN_DRAG_THRESHOLD_PX = 8;

export interface ZoomableStageHandle {
  reset: () => void;
}

interface ZoomableStageProps {
  children: React.ReactNode;
  /** Ref to attach to the inner (transformed) container. */
  innerRef: React.RefObject<HTMLDivElement | null>;
  /** Applied to the outer (non-scaled) container. */
  style?: ViewStyle;
  /** Fires when the image's scale crosses the micro-zoom snap-back
   *  threshold — useful for enabling/disabling a Fit button. */
  onScaleChange?: (scale: number) => void;
}

interface PinchStart {
  scale: number;
  tx: number;
  ty: number;
  distance: number;
  centerX: number;
  centerY: number;
  containerRect: DOMRect;
}

interface PanStart {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startTx: number;
  startTy: number;
  /** True once the movement threshold has been crossed; until
   *  then we don't claim the event, so a short tap still reaches
   *  the child. */
  active: boolean;
}

export const ZoomableStage = forwardRef<ZoomableStageHandle, ZoomableStageProps>(
  function ZoomableStage(
    { children, innerRef, style, onScaleChange },
    handleRef,
  ) {
    const outerRef = useRef<HTMLDivElement | null>(null);
    const stateRef = useRef({ scale: 1, tx: 0, ty: 0 });
    const lastReportedScaleRef = useRef(1);
    const [, setTick] = useState(0);
    const forceUpdate = useCallback(() => setTick((n) => n + 1), []);

    const pointersRef = useRef<Map<number, { x: number; y: number }>>(
      new Map(),
    );
    const pinchStartRef = useRef<PinchStart | null>(null);
    const panStartRef = useRef<PanStart | null>(null);
    const lastTapRef = useRef<{ x: number; y: number; t: number } | null>(
      null,
    );

    const writeTransform = useCallback(() => {
      const s = stateRef.current;
      const el = innerRef.current;
      if (!el) return;
      el.style.transform = `translate3d(${s.tx}px, ${s.ty}px, 0) scale(${s.scale})`;
      el.style.transformOrigin = '0 0';
    }, [innerRef]);

    useEffect(() => {
      writeTransform();
    });

    // Report scale changes that cross the snap-back threshold, so
    // consumers (like a Fit button) can flip state without polling.
    const maybeReportScale = useCallback(() => {
      if (!onScaleChange) return;
      const now = stateRef.current.scale;
      const last = lastReportedScaleRef.current;
      const wasZoomed = last > SNAP_BACK_THRESHOLD;
      const isZoomed = now > SNAP_BACK_THRESHOLD;
      if (wasZoomed !== isZoomed) {
        lastReportedScaleRef.current = now;
        onScaleChange(now);
      }
    }, [onScaleChange]);

    const clampPan = useCallback(
      (tx: number, ty: number, scale: number) => {
        const outer = outerRef.current;
        if (!outer) return { tx, ty };
        const rect = outer.getBoundingClientRect();
        const maxX = rect.width * (scale - 1);
        const maxY = rect.height * (scale - 1);
        return {
          tx: Math.max(-maxX, Math.min(0, tx)),
          ty: Math.max(-maxY, Math.min(0, ty)),
        };
      },
      [],
    );

    const resetTransform = useCallback(() => {
      stateRef.current = { scale: 1, tx: 0, ty: 0 };
      const el = innerRef.current;
      if (el) {
        el.style.transition = 'transform 200ms ease-out';
        writeTransform();
        window.setTimeout(() => {
          if (el) el.style.transition = '';
        }, 220);
      }
      forceUpdate();
      maybeReportScale();
    }, [innerRef, writeTransform, forceUpdate, maybeReportScale]);

    // Imperative handle — lets the parent call reset() directly
    // (e.g. a "Fit to screen" button in the floating toolbar).
    useImperativeHandle(handleRef, () => ({ reset: resetTransform }), [
      resetTransform,
    ]);

    const onPointerDown = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        pointersRef.current.set(e.pointerId, {
          x: e.clientX,
          y: e.clientY,
        });

        if (pointersRef.current.size === 2) {
          // Pinch start.
          const [p1, p2] = Array.from(pointersRef.current.values());
          const distance = Math.hypot(p1.x - p2.x, p1.y - p2.y) || 1;
          const outer = outerRef.current;
          const containerRect =
            outer?.getBoundingClientRect() || new DOMRect();
          pinchStartRef.current = {
            scale: stateRef.current.scale,
            tx: stateRef.current.tx,
            ty: stateRef.current.ty,
            distance,
            centerX: (p1.x + p2.x) / 2 - containerRect.left,
            centerY: (p1.y + p2.y) / 2 - containerRect.top,
            containerRect,
          };
          // When pinch starts, any pending pan ends without
          // claiming further events (it was clearly a 2-finger
          // intent from the start).
          panStartRef.current = null;
          try {
            e.currentTarget.setPointerCapture(e.pointerId);
          } catch {
            /* ok */
          }
          const inner = innerRef.current;
          if (inner) inner.style.transition = '';
          e.preventDefault();
        } else if (
          pointersRef.current.size === 1 &&
          stateRef.current.scale > SNAP_BACK_THRESHOLD
        ) {
          // Prime a potential pan — don't claim yet. The doc-level
          // move handler will decide based on the drag threshold.
          panStartRef.current = {
            pointerId: e.pointerId,
            startClientX: e.clientX,
            startClientY: e.clientY,
            startTx: stateRef.current.tx,
            startTy: stateRef.current.ty,
            active: false,
          };
        }
      },
      [innerRef],
    );

    useEffect(() => {
      const onMove = (e: PointerEvent) => {
        if (!pointersRef.current.has(e.pointerId)) return;
        pointersRef.current.set(e.pointerId, {
          x: e.clientX,
          y: e.clientY,
        });

        // Pinch mode: two pointers active.
        const ps = pinchStartRef.current;
        if (ps && pointersRef.current.size >= 2) {
          const [p1, p2] = Array.from(pointersRef.current.values());
          const distance = Math.hypot(p1.x - p2.x, p1.y - p2.y) || 1;
          const newScale = Math.max(
            MIN_SCALE,
            Math.min(MAX_SCALE, ps.scale * (distance / ps.distance)),
          );
          const mxNow = (p1.x + p2.x) / 2 - ps.containerRect.left;
          const myNow = (p1.y + p2.y) / 2 - ps.containerRect.top;
          const mdx = mxNow - ps.centerX;
          const mdy = myNow - ps.centerY;
          const ratio = newScale / ps.scale;
          const tx = mdx + ps.centerX - (ps.centerX - ps.tx) * ratio;
          const ty = mdy + ps.centerY - (ps.centerY - ps.ty) * ratio;
          const clamped = clampPan(tx, ty, newScale);
          stateRef.current = {
            scale: newScale,
            tx: clamped.tx,
            ty: clamped.ty,
          };
          const inner = innerRef.current;
          if (inner) {
            inner.style.transform = `translate3d(${clamped.tx}px, ${clamped.ty}px, 0) scale(${newScale})`;
            inner.style.transformOrigin = '0 0';
          }
          maybeReportScale();
          e.preventDefault();
          return;
        }

        // Pan mode: one pointer, scale > 1, movement past threshold.
        const pan = panStartRef.current;
        if (pan && pan.pointerId === e.pointerId) {
          const dx = e.clientX - pan.startClientX;
          const dy = e.clientY - pan.startClientY;
          if (!pan.active) {
            if (Math.hypot(dx, dy) < PAN_DRAG_THRESHOLD_PX) return;
            pan.active = true;
          }
          const clamped = clampPan(
            pan.startTx + dx,
            pan.startTy + dy,
            stateRef.current.scale,
          );
          stateRef.current = {
            scale: stateRef.current.scale,
            tx: clamped.tx,
            ty: clamped.ty,
          };
          const inner = innerRef.current;
          if (inner) {
            inner.style.transform = `translate3d(${clamped.tx}px, ${clamped.ty}px, 0) scale(${stateRef.current.scale})`;
            inner.style.transformOrigin = '0 0';
          }
          // Consume so the outer ScrollView doesn't scroll too.
          e.preventDefault();
        }
      };

      const onUp = (e: PointerEvent) => {
        pointersRef.current.delete(e.pointerId);
        if (pointersRef.current.size < 2) {
          pinchStartRef.current = null;
          if (stateRef.current.scale < SNAP_BACK_THRESHOLD) {
            resetTransform();
            return;
          }
        }
        // End pan if it was this pointer.
        const pan = panStartRef.current;
        if (pan && pan.pointerId === e.pointerId) {
          panStartRef.current = null;
        }

        // Double-tap detection when every pointer has released.
        if (pointersRef.current.size === 0 && pinchStartRef.current === null) {
          const now = performance.now();
          const last = lastTapRef.current;
          const here = { x: e.clientX, y: e.clientY, t: now };
          if (
            last &&
            now - last.t < DOUBLE_TAP_MS &&
            Math.hypot(here.x - last.x, here.y - last.y) <
              DOUBLE_TAP_SLOP_PX
          ) {
            lastTapRef.current = null;
            if (stateRef.current.scale > 1.01) {
              resetTransform();
            }
          } else {
            lastTapRef.current = here;
          }
        }
        maybeReportScale();
      };

      const onCancel = (e: PointerEvent) => {
        pointersRef.current.delete(e.pointerId);
        if (pointersRef.current.size < 2) {
          pinchStartRef.current = null;
        }
        const pan = panStartRef.current;
        if (pan && pan.pointerId === e.pointerId) {
          panStartRef.current = null;
        }
      };

      document.addEventListener('pointermove', onMove, { passive: false });
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onCancel);
      return () => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onCancel);
      };
    }, [innerRef, clampPan, resetTransform, maybeReportScale]);

    if (Platform.OS !== 'web') {
      return (
        <View ref={innerRef as any} style={[styles.outer, style]}>
          {children}
        </View>
      );
    }

    // `touch-action: pan-y` lets the parent ScrollView scroll
    // vertically on a 1-finger drag when we're NOT zoomed. At
    // scale > 1 we override to `none` via the inline style so we
    // can fully capture 1-finger pan.
    const isZoomed = stateRef.current.scale > SNAP_BACK_THRESHOLD;
    const touchAction = isZoomed ? 'none' : 'pan-y';
    return (
      <View
        {...({
          ref: outerRef,
          onPointerDown,
          style: [
            styles.outer,
            { touchAction } as any,
            style,
          ],
        } as any)}
      >
        <View
          ref={innerRef as any}
          style={[styles.inner, { willChange: 'transform' } as any]}
        >
          {children}
        </View>
      </View>
    );
  },
);

const styles = StyleSheet.create({
  outer: {
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  inner: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
});
